import type {ModelHubPage} from './ModelHubPages';

/**
 * Discriminated union describing the standalone Model Hub view rendered for the current URL. Each
 * variant maps one-to-one to a canonical route (see the path builders below); `ModelHubApp` is a pure
 * function of this value.
 */
export type ModelHubRoute =
  | {kind: 'models'}
  | {kind: 'new-prediction'; model?: string}
  // The predictions listing's batch filter lives in the query string (?predictionBatchId=), which
  // PredictionsPage reads directly, so it is intentionally not part of this route object.
  | {kind: 'predictions'}
  | {kind: 'prediction-detail'; id: string}
  | {kind: 'batches'}
  | {kind: 'batch-detail'; id: string}
  // Read-only account/settings page reached from the sidebar profile menu. Session-gated.
  | {kind: 'account'}
  // Signed-out signup wizard takeover. Served at /signup by the backend shell for standalone
  // tenants; renders outside the authenticated app chrome (see ModelHubApp).
  | {kind: 'signup'};

// Path builders — all return leading-slash absolute paths suitable for navigateToURL / Backbone.

export function modelsPath(): string {
  return '/models';
}

export function newPredictionPath(model?: string): string {
  return model != null && model !== ''
    ? `/new-prediction?model=${encodeURIComponent(model)}`
    : '/new-prediction';
}

export function predictionsPath(batchFilterId?: string): string {
  return batchFilterId != null && batchFilterId !== ''
    ? `/predictions?predictionBatchId=${encodeURIComponent(batchFilterId)}`
    : '/predictions';
}

export function predictionDetailPath(id: string): string {
  return `/predictions/${encodeURIComponent(id)}`;
}

export function batchesPath(): string {
  return '/prediction-batches';
}

export function batchDetailPath(id: string): string {
  return `/prediction-batches/${encodeURIComponent(id)}`;
}

export function accountPath(): string {
  return '/account';
}

export function signupPath(): string {
  return '/signup';
}

/**
 * Which navbar item should be highlighted for a given route. Detail views highlight their parent
 * listing; the new-prediction takeover highlights nothing (null).
 */
export function activePageForRoute(route: ModelHubRoute): ModelHubPage | null {
  if (route.kind === 'models') {
    return 'models';
  }
  if (route.kind === 'predictions' || route.kind === 'prediction-detail') {
    return 'predictions';
  }
  if (route.kind === 'batches' || route.kind === 'batch-detail') {
    return 'batches';
  }
  // new-prediction and signup are full-screen takeovers, and account is reached from the profile
  // menu — none highlight a nav item.
  return null;
}

const PAGE_PATHS: Record<ModelHubPage, string> = {
  models: modelsPath(),
  predictions: predictionsPath(),
  batches: batchesPath(),
  'new-prediction': newPredictionPath(),
};

/** Maps a navbar page key to its canonical path (used by ModelHubNavbar's onNavigate). */
export function pathForPage(page: ModelHubPage): string {
  return PAGE_PATHS[page];
}
